import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TickerDropdown.css';

interface Ticker {
  symbol: string;
  name: string;
  sector?: string;
}

interface TickerDropdownProps {
  value: string;
  onChange: (ticker: string) => void;
  onSelect: (ticker: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const TickerDropdown: React.FC<TickerDropdownProps> = ({
  value,
  onChange,
  onSelect,
  disabled = false,
  placeholder = "Enter ticker (e.g., AAPL)"
}) => {
  const [suggestions, setSuggestions] = useState<Ticker[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hardcoded popular tickers for initial suggestions and offline mode
  const popularTickers: Ticker[] = [
    { symbol: "AAPL", name: "Apple Inc.", sector: "Technology" },
    { symbol: "MSFT", name: "Microsoft Corporation", sector: "Technology" },
    { symbol: "GOOGL", name: "Alphabet Inc.", sector: "Technology" },
    { symbol: "AMZN", name: "Amazon.com Inc.", sector: "Technology" },
    { symbol: "NVDA", name: "NVIDIA Corporation", sector: "Technology" },
    { symbol: "TSLA", name: "Tesla Inc.", sector: "Technology" },
    { symbol: "META", name: "Meta Platforms", sector: "Technology" },
    { symbol: "IREN", name: "Iris Energy Limited", sector: "Technology" },
    { symbol: "JPM", name: "JPMorgan Chase", sector: "Finance" },
    { symbol: "JNJ", name: "Johnson & Johnson", sector: "Healthcare" },
    { symbol: "WMT", name: "Walmart Inc.", sector: "Consumer" },
    { symbol: "V", name: "Visa Inc.", sector: "Finance" },
    { symbol: "PG", name: "Procter & Gamble", sector: "Consumer" },
    { symbol: "MA", name: "Mastercard", sector: "Finance" },
    { symbol: "UNH", name: "UnitedHealth Group", sector: "Healthcare" },
    { symbol: "DIS", name: "Walt Disney Company", sector: "Consumer" },
    { symbol: "PYPL", name: "PayPal Holdings", sector: "Technology" },
    { symbol: "NFLX", name: "Netflix Inc.", sector: "Technology" },
    { symbol: "INTC", name: "Intel Corporation", sector: "Technology" },
    { symbol: "AMD", name: "Advanced Micro Devices", sector: "Technology" }
  ];

  // Filter tickers based on input
  const filterTickers = useCallback((query: string, tickers: Ticker[]): Ticker[] => {
    if (!query) return tickers.slice(0, 10);

    const upperQuery = query.toUpperCase();
    const lowerQuery = query.toLowerCase();

    // Sort by relevance
    const exact = tickers.filter(t => t.symbol === upperQuery);
    const startsWith = tickers.filter(t =>
      t.symbol.startsWith(upperQuery) && t.symbol !== upperQuery
    );
    const contains = tickers.filter(t =>
      t.symbol.includes(upperQuery) &&
      !t.symbol.startsWith(upperQuery)
    );
    const nameMatches = tickers.filter(t =>
      t.name.toLowerCase().includes(lowerQuery) &&
      !t.symbol.includes(upperQuery)
    );

    // Combine and deduplicate
    const combined = [...exact, ...startsWith, ...contains, ...nameMatches];
    const seen = new Set<string>();
    const unique = combined.filter(t => {
      if (seen.has(t.symbol)) return false;
      seen.add(t.symbol);
      return true;
    });

    return unique.slice(0, 10);
  }, []);

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(async (query: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://nings-api.beamer-labs.workers.dev/tickers/search?q=${encodeURIComponent(query)}&limit=10`
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.tickers || []);
      } else {
        // Fall back to local filtering
        setSuggestions(filterTickers(query, popularTickers));
      }
    } catch (error) {
      // Fall back to local filtering on error
      console.warn('Failed to fetch ticker suggestions, using local data', error);
      setSuggestions(filterTickers(query, popularTickers));
    } finally {
      setLoading(false);
    }
  }, [filterTickers]);

  // Handle input change with debounce
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.length === 0) {
      // Show popular tickers when empty
      setSuggestions(popularTickers.slice(0, 10));
      setIsOpen(false);
      return;
    }

    setIsOpen(true);

    // Use local filtering immediately for responsiveness
    setSuggestions(filterTickers(value, popularTickers));

    // Then fetch from API after debounce
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, fetchSuggestions, filterTickers]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key === 'ArrowDown') {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const ticker = suggestions[selectedIndex].symbol;
          onChange(ticker);
          onSelect(ticker);
          setIsOpen(false);
        } else if (value) {
          onSelect(value.toUpperCase());
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelectTicker = (ticker: string) => {
    onChange(ticker);
    onSelect(ticker);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className="ticker-dropdown" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => value && setIsOpen(true)}
        placeholder={placeholder}
        className="ticker-input"
        disabled={disabled}
        autoComplete="off"
      />

      {isOpen && suggestions.length > 0 && (
        <div className="dropdown-menu">
          {loading && (
            <div className="dropdown-loading">Searching...</div>
          )}
          {suggestions.map((ticker, index) => (
            <div
              key={ticker.symbol}
              className={`dropdown-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSelectTicker(ticker.symbol)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="ticker-symbol">{ticker.symbol}</div>
              <div className="ticker-name">{ticker.name}</div>
              {ticker.sector && (
                <div className="ticker-sector">{ticker.sector}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TickerDropdown;